import React, { useState } from 'react';
import {
    TrendingUp,
    TrendingDown,
    Minus,
    Shield,
    Clock,
    Activity,
    Zap,
    Target,
} from 'lucide-react';
import { formatEther } from 'viem';
import { useDashboardData, OnChainSignal } from '../../hooks/useDuckSignals';
import { useOnChainStats } from '../../hooks/useOnChainStats';
import { getTokenImageUrl } from '../../lib/tokenImages';
import { CA_ADDRESS } from '../../config/constants';

// ═══════════════════════════════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    purpleLight: '#A08BFF',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    blue: '#3b82f6',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
    glow: '0 0 40px rgba(255, 215, 0, 0.15)',
};

const styles = {
    container: {
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
    } as React.CSSProperties,
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 420px), 1fr))',
        gap: '1.25rem',
        marginTop: '1.5rem',
    } as React.CSSProperties,
    card: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '16px',
        padding: '1.25rem',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative' as const,
    } as React.CSSProperties,
    cardHover: {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 40px ${THEME.purple}40`,
    } as React.CSSProperties,
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.875rem',
        marginBottom: '0.875rem',
    } as React.CSSProperties,
    tokenImage: {
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.yellow})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        border: `2px solid ${THEME.border}`,
        overflow: 'hidden',
        flexShrink: 0,
    } as React.CSSProperties,
    tokenInfo: {
        flex: 1,
    } as React.CSSProperties,
    tokenName: {
        fontSize: '1.125rem',
        fontWeight: 700,
        color: '#fff',
        marginBottom: '0.25rem',
    } as React.CSSProperties,
    tokenSymbol: {
        fontSize: '0.875rem',
        color: '#9ca3af',
    } as React.CSSProperties,
    signalBadge: (type: string) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        borderRadius: '9999px',
        fontSize: '0.875rem',
        fontWeight: 700,
        background: type === 'BUY' ? `${THEME.green}20` : type === 'SELL' ? `${THEME.red}20` : `${THEME.yellow}20`,
        border: `1px solid ${type === 'BUY' ? `${THEME.green}40` : type === 'SELL' ? `${THEME.red}40` : `${THEME.yellow}40`}`,
        color: type === 'BUY' ? THEME.green : type === 'SELL' ? THEME.red : THEME.yellow,
    } as React.CSSProperties),
    stats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.5rem',
        marginTop: '1rem',
        padding: '0.875rem',
        background: 'rgba(0,0,0,0.3)',
        borderRadius: '12px',
    } as React.CSSProperties,
    stat: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '0.25rem',
    },
    statLabel: {
        fontSize: '0.7rem',
        color: '#9ca3af',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
    } as React.CSSProperties,
    statValue: {
        fontSize: '1rem',
        fontWeight: 700,
        color: '#fff',
    } as React.CSSProperties,
    confidence: {
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'rgba(131, 110, 249, 0.1)',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    } as React.CSSProperties,
    confidenceBar: {
        width: '100%',
        height: '6px',
        background: 'rgba(0,0,0,0.4)',
        borderRadius: '9999px',
        overflow: 'hidden',
        position: 'relative' as const,
    } as React.CSSProperties,
    confidenceProgress: (value: number) => ({
        width: `${value}%`,
        height: '100%',
        background: value >= 70 ? `linear-gradient(90deg, ${THEME.green}, ${THEME.yellow})` : value >= 40 ? `linear-gradient(90deg, ${THEME.orange}, ${THEME.yellow})` : `linear-gradient(90deg, ${THEME.red}, ${THEME.orange})`,
        transition: 'width 0.5s ease',
    } as React.CSSProperties),
    reason: {
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#d1d5db',
        lineHeight: 1.5,
    } as React.CSSProperties,
    footer: {
        marginTop: '1rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: '1rem',
        borderTop: '1px solid rgba(255,255,255,0.05)',
    } as React.CSSProperties,
    agent: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontSize: '0.75rem',
        color: '#9ca3af',
    } as React.CSSProperties,
    time: {
        fontSize: '0.75rem',
        color: '#6b7280',
    } as React.CSSProperties,
    emptyState: {
        textAlign: 'center' as const,
        padding: '4rem 2rem',
        color: '#9ca3af',
    } as React.CSSProperties,
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function timeAgo(timestamp: bigint): string {
    const now = Date.now() / 1000;
    const diff = now - Number(timestamp);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

function formatAddress(addr: string): string {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ENHANCED SIGNAL CARD
// ═══════════════════════════════════════════════════════════════════════════════

interface SignalCardProps {
    signal: OnChainSignal;
    priceUsd: number;
    volume24h: number;
    marketCap: number;
    liquidity: number;
}

const EnhancedSignalCard: React.FC<SignalCardProps> = ({ signal, priceUsd, volume24h, marketCap, liquidity }) => {
    const [isHovered, setIsHovered] = useState(false);

    const SignalIcon = signal.signalType === 'BUY' ? TrendingUp : signal.signalType === 'SELL' ? TrendingDown : Minus;

    return (
        <div
            style={{
                ...styles.card,
                ...(isHovered ? styles.cardHover : {}),
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Header with Token */}
            <div style={styles.header}>
                <div style={styles.tokenImage}>
                    <img
                        src={getTokenImageUrl('DUCK', CA_ADDRESS)}
                        alt="DUCK"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}
                    />
                </div>
                <div style={styles.tokenInfo}>
                    <div style={styles.tokenName}>DUCK</div>
                    <div style={styles.tokenSymbol}>Duck Token on Monad</div>
                </div>
                <div style={styles.signalBadge(signal.signalType)}>
                    <SignalIcon size={16} />
                    {signal.signalType}
                </div>
            </div>

            {/* Price Info - All from real DexScreener data */}
            <div style={styles.stats}>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Signal Price</div>
                    <div style={styles.statValue}>
                        {Number(formatEther(signal.price)).toFixed(8)} MON
                    </div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>USD Price</div>
                    <div style={styles.statValue}>
                        {priceUsd > 0 ? `$${priceUsd.toFixed(6)}` : 'N/A'}
                    </div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Market Cap</div>
                    <div style={styles.statValue}>
                        {marketCap > 0 ? `$${(marketCap / 1000).toFixed(1)}K` : 'N/A'}
                    </div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>24h Volume</div>
                    <div style={styles.statValue}>
                        {volume24h > 0 ? `$${(volume24h / 1000).toFixed(1)}K` : 'N/A'}
                    </div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Liquidity</div>
                    <div style={styles.statValue}>
                        {liquidity > 0 ? `$${(liquidity / 1000).toFixed(1)}K` : 'N/A'}
                    </div>
                </div>
            </div>

            {/* Confidence */}
            <div style={styles.confidence}>
                <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.75rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Confidence Score
                        </span>
                        <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff' }}>
                            {Number(signal.confidence)}%
                        </span>
                    </div>
                    <div style={styles.confidenceBar}>
                        <div style={styles.confidenceProgress(Number(signal.confidence))} />
                    </div>
                </div>
            </div>

            {/* Reason */}
            {signal.reason && (
                <div style={styles.reason}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <Target size={14} color={THEME.yellow} />
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af' }}>
                            Analysis
                        </span>
                    </div>
                    {signal.reason}
                </div>
            )}

            {/* Footer */}
            <div style={styles.footer}>
                <div style={styles.agent}>
                    <Zap size={14} color={THEME.purple} />
                    <span>Agent</span>
                    <span style={{ color: '#4b5563' }}>•</span>
                    <span>{formatAddress(signal.agent)}</span>
                </div>
                <div style={styles.time}>
                    <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    {timeAgo(signal.timestamp)}
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const EnhancedTokenFeed: React.FC<{ tokenAddress?: string }> = ({ tokenAddress }) => {
    const { isDeployed, isLoading, recentSignals } = useDashboardData();
    const stats = useOnChainStats(tokenAddress, 900_000);

    if (!isDeployed) {
        return (
            <div style={styles.emptyState}>
                <Shield size={64} color={THEME.purple} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: '0 0 0.5rem' }}>
                    Contract Not Deployed
                </h3>
                <p>DuckSignals contract has not been deployed yet. Deploy the contract to see live signals.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={styles.emptyState}>
                <Activity size={64} color={THEME.purple} style={{ marginBottom: '1rem', opacity: 0.5, animation: 'pulse 2s infinite' }} />
                <p>Loading blockchain data...</p>
            </div>
        );
    }

    if (!recentSignals || recentSignals.length === 0) {
        return (
            <div style={styles.emptyState}>
                <TrendingUp size={64} color={THEME.purple} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: '0 0 0.5rem' }}>
                    No Signals Yet
                </h3>
                <p>AI agents post signals every 15 minutes. Check back soon!</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.grid}>
                {recentSignals.map((signal, index) => (
                    <EnhancedSignalCard
                        key={index}
                        signal={signal}
                        priceUsd={stats.priceUsd}
                        volume24h={stats.volume24h}
                        marketCap={stats.marketCap}
                        liquidity={stats.liquidity}
                    />
                ))}
            </div>
        </div>
    );
};

export default EnhancedTokenFeed;
